import React from 'react';
import {
  Feedback,
  fieldFeedbackContainer,
  requiredLabel,
} from '@noths/polaris-client-ribbons-design-system';
import { CurrencyCode } from '@noths/polaris-dev-ts-types';

import {
  InteractionErrorTrackingEvent,
  InteractionTrackingEvent,
  ProgressTrackingEvent,
  SelectionPayload,
  TrackedEvents,
} from 'src/redux/personalisation';
import { ProductOption, ProductOptionValue } from 'src/types';
import { MIN_PERSONALISATIONS_OPTIONS_FOR_EXPANDING } from '../../../ProductInfo/constants';
import { REQUIRED_SELECTION_COPY } from '../../constants/copy';
import { getFieldPosition, getProgressMilestone } from '../../utils';
import { SelectedOptionValueLabel } from '../SelectedOptionValueLabel';
import * as styles from '../../styles/Personalisation.styles';

const getId = (id: number) => `${id}`;

export interface OwnProps extends ProductOption {
  displayValidation: boolean | undefined;
}

export interface DispatchProps {
  trackInteraction: (payload: InteractionTrackingEvent) => void;
  trackProgressMilestone: (payload: ProgressTrackingEvent) => void;
  trackInteractionError: (payload: InteractionErrorTrackingEvent) => void;
  toggleSelectPersonalisation: (payload: SelectionPayload) => void;
}

interface ContainerProps extends DispatchProps {
  currency: CurrencyCode;
  error: boolean;
  isExpanded?: boolean;
  options: ProductOption[];
  selectedOption?: ProductOptionValue[];
  totalOutstandingSelections: number;
  totalRequiredPersonalisations: number;
  trackedEvents: TrackedEvents;
  toggleExpanded: () => void;
}

type Props = OwnProps & DispatchProps & ContainerProps;

interface HandleOnChangeFnParams extends Omit<ContainerProps, 'error' | 'currency'> {
  appliedValue: ProductOptionValue;
  id: number;
  name: string;
  required: boolean;
  value: ProductOptionValue;
}

const handleChange = ({
  appliedValue,
  id,
  isExpanded,
  name,
  required,
  options,
  totalOutstandingSelections,
  totalRequiredPersonalisations,
  trackedEvents,
  value,
  toggleSelectPersonalisation,
  trackInteraction,
  trackProgressMilestone,
  toggleExpanded,
}: HandleOnChangeFnParams) => {
  const fieldName = name;
  const fieldPosition = getFieldPosition(options, { id });

  trackInteraction({
    field_position: fieldPosition,
    field_name: fieldName,
    field_action: 'Field Input',
    field_type: 'single & binary selection',
    field_value: value.id === appliedValue?.id ? null : value.name,
    required_field: required,
  });

  toggleSelectPersonalisation({
    optionId: id,
    optionValue: value,
    type: 'single-selection',
  });

  const milestone = getProgressMilestone({
    totalRequiredPersonalisations,
    totalOutstandingSelections,
    trackedEvents,
    fieldName,
    isRequiredPersonalisation: required,
  });

  if (milestone) {
    trackProgressMilestone({
      field_position: fieldPosition,
      field_name: fieldName,
      field_action: milestone,
      field_type: 'single & binary selection',
      field_value: name,
      required_field: true,
    });
  }

  if (!isExpanded && options.length >= MIN_PERSONALISATIONS_OPTIONS_FOR_EXPANDING) {
    toggleExpanded();
  }
};

export const SelectionSingleOrBinary = ({
  currency,
  error,
  isExpanded,
  options,
  totalOutstandingSelections,
  totalRequiredPersonalisations,
  trackedEvents,
  toggleSelectPersonalisation,
  trackInteraction,
  trackProgressMilestone,
  trackInteractionError,
  toggleExpanded,
  ...props
}: Props) => {
  const [appliedValue] = props.selectedOption ?? [];

  const handleOnChange = (value: ProductOptionValue) => {
    handleChange({
      value,
      id: props.id,
      isExpanded,
      name: props.name,
      appliedValue,
      trackedEvents,
      options,
      required: props.required,
      totalOutstandingSelections,
      totalRequiredPersonalisations,
      toggleSelectPersonalisation,
      toggleExpanded,
      trackInteraction,
      trackProgressMilestone,
      trackInteractionError,
    });
  };

  const errorContainerId = `selection-error-${props.id}`;

  return (
    <div
      css={[styles.radioGroup, styles.selectionContainer]}
      role="group"
      aria-labelledby={getId(props.id)}
      data-has-error={error}
      data-is-valid={appliedValue && !error}
      aria-describedby={errorContainerId}
    >
      {props.required && (
        <p css={requiredLabel} data-has-error={error} aria-hidden="true">
          Required
        </p>
      )}
      <p
        css={styles.groupLabel}
        id={getId(props.id)}
        dangerouslySetInnerHTML={{ __html: props.name }}
      />
      {props.values?.map((value) => (
        <SelectedOptionValueLabel
          inputType={props.values!.length === 1 ? 'checkbox' : 'radio'}
          currency={currency}
          key={value.id}
          value={value}
          name={getId(props.id)}
          isSelected={value.id === appliedValue?.id}
          error={error}
          onChange={() => handleOnChange(value)}
          onFocus={() => {
            if (!trackedEvents.focus[props.name]) {
              trackInteraction({
                field_position: getFieldPosition(options, props),
                field_name: props.name,
                field_action: 'Field Focus',
                field_type: 'single & binary selection',
                field_value: value.name,
                required_field: props.required,
              });
            }
          }}
        />
      ))}
      <div css={[fieldFeedbackContainer, styles.feedbackContainer]} id={errorContainerId}>
        {error && <Feedback name={REQUIRED_SELECTION_COPY} value="" error iconAlign="left" />}
      </div>
    </div>
  );
};
